import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/auth';

interface AuthGuardProps {
  children: React.ReactNode;
  requireEmailVerificationOnly?: boolean;
}

export function AuthGuard({ children, requireEmailVerificationOnly = false }: AuthGuardProps) {
  const { isAuthenticated, parent } = useAuthStore()
  const location = useLocation()

  // If not authenticated, redirect to login
  if (!isAuthenticated || !parent) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // If authenticated but email not verified, redirect to verification page
  // unless they're already on the verification page
  if (!parent.is_email_verified && location.pathname !== '/verification-pending') {
    return <Navigate to="/verification-pending" replace />
  }

  // If authenticated and email verified, but trying to access verification page, redirect to home
  if (parent.is_email_verified && location.pathname === '/verification-pending') {
    return <Navigate to="/" replace />
  }

  // If authenticated and email verified, but profile not complete, redirect to profile completion page
  // unless they're already on the profile completion page or we're only requiring email verification
  if (parent.is_email_verified && !parent.is_profile_complete && 
      location.pathname !== '/profile-completion' && !requireEmailVerificationOnly) {
    return <Navigate to="/profile-completion" replace />
  }

  // If authenticated, email verified, and profile complete, but trying to access profile completion page, redirect to home
  if (parent.is_email_verified && parent.is_profile_complete && location.pathname === '/profile-completion') {
    return <Navigate to="/" replace />
  }

  // If all checks pass, render the children
  return <>{children}</>
} 